/**
 * Designer Bridge Script
 * 
 * Handles communication between Shopify parent page and embedded designer IFrame.
 * Add this file to your Shopify theme's assets folder.
 * 
 * Features:
 * - Handshake protocol with retry and timeout
 * - postMessage origin validation
 * - Cart add integration via Shopify AJAX API
 */

(function () {
    'use strict';

    // === CONFIGURATION ===
    // Multi-app registry: window.NfckeyStudioApps is injected sitewide from
    // snippets/nfckey-cart-persistence.liquid (shop metafield nfckey.studio_apps,
    // [{ key, name, origin, proxyPath }, ...]). Any registered app's iframe is
    // trusted; the matched app's key is stamped onto cart lines so every
    // downstream surface routes the line back to the app that created it.
    // Falls back to a built-in prod entry if the metafield is missing.
    // Trusted apps come solely from THIS store's own registry (shop metafield).
    // No hardcoded prod fallback: an unconfigured store trusts no origin
    // (fail-closed) rather than silently trusting/pointing at the prod app.
    const STUDIO_APPS = (function () {
        return Array.isArray(window.NfckeyStudioApps) ? window.NfckeyStudioApps.filter(function (a) {
            if (!a || typeof a.key !== 'string' || typeof a.origin !== 'string') return false;
            try { return new URL(a.origin).origin === a.origin; } catch (_) { return false; }
        }) : [];
    })();
    // Default app for lines/messages that don't resolve to a specific app.
    // Delegates to the shared resolver emitted by nfckey-cart-persistence.liquid
    // so there is one rule for this in the theme (#617). It never falls back to
    // registry[0]: on the production store that is `dev`, so a positional pick
    // could silently route real customers to dev.studio.nfckey.com.
    const EMPTY_APP = { key: '', name: '', origin: '', proxyPath: '' };
    const FALLBACK_APP = (function () {
        if (typeof window.NfckeyResolveStudioApp === 'function') {
            return window.NfckeyResolveStudioApp(window.NfckeyStudioDefaultAppKey) || EMPTY_APP;
        }
        // Resolver not yet defined (unexpected load order): match by key only,
        // and return empty rather than guessing.
        var keys = [window.NfckeyStudioDefaultAppKey, 'prod'];
        for (var k = 0; k < keys.length; k++) {
            if (!keys[k]) continue;
            for (var i = 0; i < STUDIO_APPS.length; i++) {
                if (STUDIO_APPS[i].key === keys[k]) return STUDIO_APPS[i];
            }
        }
        return EMPTY_APP;
    })();

    function findAppByOrigin(origin) {
        for (var i = 0; i < STUDIO_APPS.length; i++) {
            if (STUDIO_APPS[i].origin === origin) return STUDIO_APPS[i];
        }
        return null;
    }

    const ALLOWED_CHILD_ORIGINS = STUDIO_APPS.map(function (a) { return a.origin; });

    const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const SAFE_HASH_RE = /^[a-zA-Z0-9_\-:]{1,128}$/;

    function sanitizeHttpUrl(value) {
        if (typeof value !== 'string' || !value) return '';
        try {
            var parsed = new URL(value);
            if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return '';
            return parsed.toString();
        } catch (_) {
            return '';
        }
    }

    function batchCartPreviewUrl(appOrigin, batchId, side) {
        return appOrigin + '/api/batches/' + batchId + '/preview?side=' + (side || 'front');
    }

    const HANDSHAKE_RETRY_MS = 500;
    const HANDSHAKE_TIMEOUT_MS = 10000;
    const GUEST_ID_RE = /^guest_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    /**
     * Issue #556 — mint a verified session via App Proxy (HMAC + Shopify
     * logged_in_customer_id) and deliver it to the iframe. The iframe must not
     * self-assert customer identity in request bodies.
     */
    function mintAndDeliverEmbedSession(childOrigin) {
        var app = findAppByOrigin(childOrigin);
        if (!app || !app.proxyPath || !iframe || !iframe.contentWindow) return;

        var shop =
            (window.Shopify && window.Shopify.shop) ||
            (typeof window.NFCKEY_SHOP === 'string' && window.NFCKEY_SHOP) ||
            '';
        var guestId = null;
        if (shop) {
            try {
                var stored = localStorage.getItem('nfc-guest-id-' + shop);
                if (stored && GUEST_ID_RE.test(stored)) guestId = stored;
            } catch (_) { /* blocked storage */ }
        }

        fetch(app.proxyPath + '/api/embed/session', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify(guestId ? { guestId: guestId } : {}),
        })
            .then(function (res) {
                if (!res.ok) throw new Error('session_mint_failed_' + res.status);
                return res.json();
            })
            .then(function (data) {
                if (!data || !data.token || !data.customerId || !data.shop) {
                    throw new Error('session_mint_incomplete');
                }
                if (String(data.customerId).indexOf('guest_') === 0 && shop) {
                    try {
                        localStorage.setItem('nfc-guest-id-' + shop, data.customerId);
                    } catch (_) { /* ignore */ }
                }
                iframe.contentWindow.postMessage(
                    {
                        type: 'EMBED_SESSION',
                        token: data.token,
                        customerId: data.customerId,
                        shop: data.shop,
                    },
                    childOrigin
                );
            })
            .catch(function (err) {
                console.warn('[Designer Bridge] Embed session mint failed:', err && err.message ? err.message : err);
            });
    }

    function parseVariantId(value) {
        if (value === undefined || value === null) return null;
        var s = String(value);
        if (s.includes('gid://')) {
            s = s.split('/').pop() || '';
        }
        var parsed = Number.parseInt(s, 10);
        if (!Number.isFinite(parsed) || parsed <= 0) return null;
        return parsed;
    }

    async function addItemsToCart(items) {
        var response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: items })
        });

        if (!response.ok) {
            var errorData = await response.json().catch(function () { return {}; });
            throw new Error(errorData.description || 'Failed to add to cart');
        }
    }

    /** Hidden cart keys only — never write visible design_id / preview_url. */
    function buildStudioCartProperties(payload, appKey) {
        var properties = payload.properties || {};
        var designId = properties['_design_id'] || payload.designId || '';
        var previewUrl = sanitizeHttpUrl(payload.previewUrl)
            || sanitizeHttpUrl(properties['_preview_url'])
            || sanitizeHttpUrl(properties['preview_url']);

        var cartProperties = {
            '_design_id': designId,
            '_source': properties['_source'] || 'embed',
            '_studio_app_key': appKey || FALLBACK_APP.key
        };

        if (previewUrl) {
            cartProperties['_preview_url'] = previewUrl;
        }
        if (properties['Design Name']) {
            cartProperties['Design Name'] = properties['Design Name'];
        }
        if (properties['Design ID']) {
            cartProperties['Design ID'] = properties['Design ID'];
        }
        if (properties['_front_image_url']) {
            cartProperties['_front_image_url'] = properties['_front_image_url'];
        }
        if (properties['_back_image_url']) {
            cartProperties['_back_image_url'] = properties['_back_image_url'];
        }
        if (properties['_keyfob_thumbnail_url']) {
            cartProperties['_keyfob_thumbnail_url'] = properties['_keyfob_thumbnail_url'];
        }
        if (properties['Memory']) {
            cartProperties['Memory'] = properties['Memory'];
        }
        if (properties['UID Size']) {
            cartProperties['UID Size'] = properties['UID Size'];
        }

        return cartProperties;
    }

    window.ShopifyDesignerBridge = {
        addToCart: async function (params) {
            var variantId = parseVariantId(params && params.variantId);
            var quantity = Number(params && params.quantity);
            if (!variantId) {
                throw new Error('Invalid variant id');
            }
            if (!Number.isInteger(quantity) || quantity <= 0) {
                quantity = 1;
            }

            // Programmatic API — resolve the app from the designer iframe's
            // src (the "active" app), falling back to the default key.
            var apiAppKey = FALLBACK_APP.key;
            try {
                var apiIframe = document.getElementById('designer-iframe');
                var iframeSrc = apiIframe && (apiIframe.src || apiIframe.dataset.src);
                var apiApp = iframeSrc ? findAppByOrigin(new URL(iframeSrc).origin) : null;
                if (apiApp) apiAppKey = apiApp.key;
            } catch (_) { /* keep fallback */ }

            var cartProperties = buildStudioCartProperties({
                designId: params && params.properties && params.properties['_design_id'],
                previewUrl: params && params.properties && (params.properties['_preview_url'] || params.properties['preview_url']),
                properties: (params && params.properties) || {}
            }, apiAppKey);

            await addItemsToCart([{
                id: variantId,
                quantity: quantity,
                properties: cartProperties
            }]);

            window.location.href = '/cart';
        }
    };

    // === DOM ELEMENTS ===
    const iframe = document.getElementById('designer-iframe');
    const loading = document.getElementById('designer-loading');
    const errorDiv = document.getElementById('designer-error');
    const errorMessage = document.getElementById('designer-error-message');

    if (!iframe) {
        return;
    }

    // === STATE ===
    let handshakeComplete = false;
    let handshakeStartTime = null;
    let handshakeInterval = null;

    // === HANDSHAKE ===
    function attemptHandshake() {
        if (handshakeComplete) {
            clearInterval(handshakeInterval);
            return;
        }

        const elapsed = Date.now() - handshakeStartTime;
        if (elapsed > HANDSHAKE_TIMEOUT_MS) {
            clearInterval(handshakeInterval);
            showError('Failed to connect to designer. Please refresh the page.');
            return;
        }

        // Send handshake request to each allowed origin
        ALLOWED_CHILD_ORIGINS.forEach(function (origin) {
            iframe.contentWindow.postMessage(
                { type: 'EMBED_HANDSHAKE_REQUEST' },
                origin
            );
        });
    }

    // Start handshake when iframe loads (skip re-fire on in-iframe navigations e.g. /embed → /embed/proof).
    iframe.addEventListener('load', function () {
        if (handshakeComplete) return;
        handshakeStartTime = Date.now();
        handshakeInterval = setInterval(attemptHandshake, HANDSHAKE_RETRY_MS);
        attemptHandshake(); // Immediate first attempt
    });

    // === MESSAGE HANDLER ===
    window.addEventListener('message', function (event) {
        // Validate origin against the registry; the matched app identifies
        // which backend created the design (stamped as _studio_app_key).
        var senderApp = findAppByOrigin(event.origin);
        if (!senderApp) {
            return;
        }

        var data = event.data;
        if (!data || typeof data.type !== 'string') {
            return;
        }

        switch (data.type) {
            case 'EMBED_HANDSHAKE_RESPONSE':
                if (!handshakeComplete) {
                    handshakeComplete = true;
                    clearInterval(handshakeInterval);
                    if (loading) loading.classList.add('hidden');
                    iframe.classList.add('loaded');
                    console.log('[Designer Bridge] Handshake complete');
                    mintAndDeliverEmbedSession(event.origin);
                }
                break;

            case 'DESIGN_COMPLETE':
                handleDesignComplete(data.payload, senderApp);
                break;

            case 'DESIGN_APPROVED':
                handleDesignApproved(data.payload || data, senderApp);
                break;

            case 'DESIGN_ERROR':
                showError(data.payload && data.payload.message || 'An error occurred');
                break;
        }
    });

    // === PROOF-GATED CART INTEGRATION ===
    async function handleDesignApproved(data, senderApp) {
        var batchId = data.batchId;
        var approvalId = data.approvalId;
        var variantId = data.variantId;
        var quantity = data.quantity;
        var designVersionHash = data.designVersionHash;
        var appOrigin = (senderApp && senderApp.origin) || FALLBACK_APP.origin;
        var frontImageUrl = sanitizeHttpUrl(data.frontImageUrl);
        var backImageUrl = sanitizeHttpUrl(data.backImageUrl);
        var keyfobThumbnailUrl = sanitizeHttpUrl(data.keyfobThumbnailUrl);
        if (!frontImageUrl && batchId) {
            frontImageUrl = batchCartPreviewUrl(appOrigin, batchId, 'front');
        }
        if (!backImageUrl && batchId) {
            backImageUrl = batchCartPreviewUrl(appOrigin, batchId, 'back');
        }

        // Validate payload before touching the cart.
        if (!UUID_RE.test(batchId || '')) {
            sendApprovedAck(false, 'Invalid batchId');
            showError('Cart add failed: invalid proof data.');
            return;
        }
        if (!UUID_RE.test(approvalId || '')) {
            sendApprovedAck(false, 'Invalid approvalId');
            showError('Cart add failed: invalid approval data.');
            return;
        }
        var numericVariantId = Number(String(variantId).replace(/^gid:\/\/shopify\/ProductVariant\//, ''));
        if (!Number.isFinite(numericVariantId) || numericVariantId <= 0) {
            sendApprovedAck(false, 'Invalid variantId');
            showError('Cart add failed: product variant not found.');
            return;
        }
        var qty = Number(quantity);
        if (!Number.isInteger(qty) || qty <= 0) {
            sendApprovedAck(false, 'Invalid quantity');
            showError('Cart add failed: invalid quantity.');
            return;
        }
        if (designVersionHash && !SAFE_HASH_RE.test(designVersionHash)) {
            sendApprovedAck(false, 'Invalid designVersionHash');
            showError('Cart add failed: invalid design version hash.');
            return;
        }

        // #440 — the branding-removal fee line.
        //
        // Both halves or neither. A design line flagged `_branding_removed`
        // with no fee line beside it becomes a paid order the webhook refuses,
        // and the customer waits for a human — so an unresolvable fee variant
        // means we author neither, exactly as the editor already decides when
        // it hides the option.
        //
        // Neither property is trusted downstream. `_branding_removal_for` is a
        // customer-authored POINTER telling us which design a fee belongs to;
        // whether anyone actually paid is decided in the orders/paid handler
        // from the variant Shopify recorded (src/lib/branding-entitlement.ts).
        var brandingFeeVariantId = data.brandingRemoved === true
            ? parseVariantId(data.brandingRemovalVariantId)
            : null;

        var designProperties = {
            _batch_id: batchId,
            _approval_id: approvalId,
            _design_version_hash: designVersionHash || '',
            _source: 'embed_proof',
            _studio_app_key: (senderApp && senderApp.key) || FALLBACK_APP.key,
            'Proof Status': 'Approved',
            '_front_image_url': frontImageUrl,
            '_back_image_url': backImageUrl
        };

        // Cart thumbnail (live-store code captured in #597): when the editor
        // supplies a composited keyfob thumbnail, cart-products.liquid renders
        // it eagerly instead of round-tripping /api/batches/{id}/preview.
        if (keyfobThumbnailUrl) {
            designProperties['_keyfob_thumbnail_url'] = keyfobThumbnailUrl;
        }

        var cartItems = [{
            id: numericVariantId,
            quantity: qty,
            properties: designProperties
        }];

        if (brandingFeeVariantId) {
            designProperties['_branding_removed'] = '1';
            cartItems.push({
                id: brandingFeeVariantId,
                // #700: per keyfob, not per design — so this mirrors the design
                // line's quantity exactly, and `branding-fee-pairing.js` keeps
                // the two equal when the customer edits the cart. They arriving
                // unequal at `orders/paid` refuses the order.
                quantity: qty,
                properties: {
                    _branding_removal_for: batchId,
                    _studio_app_key: (senderApp && senderApp.key) || FALLBACK_APP.key
                }
            });
        }

        try {
            var response = await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items: cartItems })
            });

            if (!response.ok) {
                var errorData = await response.json().catch(function () { return {}; });
                throw new Error(errorData.description || 'Failed to add to cart');
            }

            sendApprovedAck(true, null);
            window.location.href = '/cart';
        } catch (err) {
            console.error('[Designer Bridge] DESIGN_APPROVED cart error:', err);
            sendApprovedAck(false, err.message || 'Cart add failed');
            // Surface Shopify's own reason, not just "please try again". This add
            // is ATOMIC over both lines (design + branding-removal fee), so a
            // single bad variant id fails the whole thing — and the generic
            // message made that indistinguishable from a network blip, which is
            // exactly the ambiguity that made a "cannot find variant" report
            // undiagnosable. `errorData.description` is Shopify's text, e.g.
            // "The variant 'X' cannot be found"; it names no internals.
            showError(
                err && err.message
                    ? 'Failed to add approved design to cart: ' + err.message
                    : 'Failed to add approved design to cart. Please try again.'
            );
        }
    }

    function sendApprovedAck(ok, message) {
        if (!iframe || !iframe.contentWindow) return;
        ALLOWED_CHILD_ORIGINS.forEach(function (origin) {
            iframe.contentWindow.postMessage(
                { type: 'DESIGN_APPROVED_ACK', ok: ok, message: message || undefined },
                origin
            );
        });
    }

    // === LEGACY CART INTEGRATION ===
    async function handleDesignComplete(payload, senderApp) {
        if (!payload) {
            showError('Invalid design data');
            return;
        }

        var designId = payload.designId;
        var previewUrl = payload.previewUrl;
        var variantId = payload.variantId;
        var quantity = payload.quantity || 1;
        var properties = payload.properties || {};

        var numericVariantId = Number(String(variantId).replace(/^gid:\/\/shopify\/ProductVariant\//, ''));
        if (!designId || !Number.isFinite(numericVariantId) || numericVariantId <= 0) {
            showError('Missing design or product information');
            return;
        }

        try {
            var cartProperties = buildStudioCartProperties(payload, senderApp && senderApp.key);
            var qty = Number(quantity);
            if (!Number.isInteger(qty) || qty <= 0) {
                qty = 1;
            }

            await addItemsToCart([{
                id: numericVariantId,
                quantity: qty,
                properties: cartProperties
            }]);

            // Success - redirect to cart
            window.location.href = '/cart';

        } catch (err) {
            console.error('[Designer Bridge] Cart error:', err);
            showError('Failed to add to cart. Please try again.');
        }
    }

    // === ERROR DISPLAY ===
    function showError(message) {
        if (loading) loading.classList.add('hidden');
        if (errorMessage) errorMessage.textContent = message;
        if (errorDiv) errorDiv.style.display = 'block';
    }

})();
